import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import Tooltip from '../ToolTip/ToolTip';
import IconCollection from '../IconCollection/IconCollection';

import css from './VerificationBadges.module.css';

const VerificationBadges = ({
  usersEmailVerified={},
  isEmailVerified,
  isIdVerified,
  className,
  badgeClassName,
  emailBadgeClassName,
  idBadgeClassName,
  tooltipPlacement = 'bottom',
  tooltipMode = 'light',
  tooltipSize = 'xl',
}) => {
  const intl = useIntl();

  if (!isEmailVerified && !isIdVerified) {
    return null;
  }



  const accountVerifiedTooltip = (usersEmailVerified) => {
    const { isEmailVerified = false, isStripeVerified = false, isCompletedTransitions = false, isPhoneVerified = false } = usersEmailVerified?.user || {};
    return (
      <div className={css.verificationTooltip}>
        <div className={css.verificationTooltipHeader}>
          <span className={css.verificationTooltipBadge}>
            <IconCollection name="verifiedIcon" />
          </span>
          <div>
            <p className={css.verificationTooltipTitle}>
              {intl.formatMessage({
                id: 'DetailCardImage.accountVerifiedTitle',
                defaultMessage: 'Account Verified',
              })}
            </p>
          </div>
        </div>
        <ul className={css.verificationTooltipList}>
          {isEmailVerified && <li className={css.verificationTooltipItem}>
            <span className={css.verificationTooltipIcon} aria-hidden="true">
              <i className="ri-mail-check-line" />
            </span>
            {intl.formatMessage({
              id: 'DetailCardImage.accountVerifiedEmail',
              defaultMessage: 'Email has been verified',
            })}
          </li>}
          {isPhoneVerified && <li className={css.verificationTooltipItem}>
            <span className={css.verificationTooltipIcon} aria-hidden="true">
              <i className="ri-phone-line" />
            </span>
            {intl.formatMessage({
              id: 'DetailCardImage.accountVerifiedPhone',
              defaultMessage: 'Mobile number has been verified',
            })}
          </li>}
          {isStripeVerified && <li className={css.verificationTooltipItem}>
            <span className={css.verificationTooltipIcon} aria-hidden="true">
              <i className="ri-secure-payment-line" />
            </span>
            {intl.formatMessage({
              id: 'DetailCardImage.accountVerifiedPayment',
              defaultMessage: 'Payment method verified by Stripe',
            })}
          </li>}
          {isCompletedTransitions && <li className={css.verificationTooltipItem}>
            <span className={css.verificationTooltipIcon} aria-hidden="true">
              <i className="ri-medal-line" />
            </span>
            {intl.formatMessage({
              id: 'DetailCardImage.accountVerifiedRental',
              defaultMessage: 'Successfully completed a rental',
            })}
          </li>}
        </ul>
        <p className={css.verificationTooltipFooter}>
          {intl.formatMessage({
            id: 'DetailCardImage.accountVerifiedFooter',
            defaultMessage:
              'This account has completed the above verification steps to ensure safer, more trusted transactions.',
          })}
        </p>
      </div>
    );
  }

  const idVerifiedTooltip = (
    <div className={css.verificationTooltip}>
      <div className={css.verificationTooltipHeader}>
        <span className={classNames(css.verificationTooltipBadge, css.verificationTooltipBadgeInfo)}>
          <i className="ri-shield-user-fill" aria-hidden="true" />
        </span>
        <p className={css.verificationTooltipTitle}>
          {intl.formatMessage({
            id: 'DetailCardImage.visualIdTitle',
            defaultMessage: 'Visual ID Verified',
          })}
        </p>
      </div>
      <div className={css.verificationTooltipItem}>
        <span
          className={classNames(css.verificationTooltipIcon, css.verificationTooltipIconAlert)}
          aria-hidden="true"
        >
          <i className="ri-user-line" />
        </span>
        {intl.formatMessage({
          id: 'DetailCardImage.visualIdSubtitle',
          defaultMessage: 'User has completed live ID verification',
        })}
      </div>
      <p className={css.verificationTooltipDescription}>
        {intl.formatMessage({
          id: 'DetailCardImage.visualIdDescription',
          defaultMessage:
            "We’ve confirmed this user’s identity through a secure, government ID and live visual check to help keep our community safe.",
        })}
      </p>
    </div>
  );

  const wrapperClassName = classNames(css.badges, className);
  const baseBadgeClass = classNames(css.badge, badgeClassName);
  const emailBadgeClasses = classNames(baseBadgeClass, css.badgeVerified, emailBadgeClassName);
  const idBadgeClasses = classNames(baseBadgeClass, css.badgeId, idBadgeClassName);

  return (
    <div className={wrapperClassName}>
      {isEmailVerified ? (
        <Tooltip
          placement={tooltipPlacement}
          mode={tooltipMode}
          size={tooltipSize}
          content={accountVerifiedTooltip(usersEmailVerified)}
        >
          <span className={emailBadgeClasses}>
            <IconCollection name="verifiedIcon" />
            <FormattedMessage id="AboutOwner.verified" defaultMessage="Verified" />
          </span>
        </Tooltip>
      ) : null}
      {isIdVerified ? (
        <Tooltip
          placement={tooltipPlacement}
          mode={tooltipMode}
          size={tooltipSize}
          content={idVerifiedTooltip}
        >
          <span className={idBadgeClasses}>
            <i className="ri-shield-user-fill" aria-hidden="true" />
            <FormattedMessage id="DetailCardImage.idVerified" defaultMessage="ID Verified" />
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
};

VerificationBadges.defaultProps = {
  className: null,
  badgeClassName: null,
  emailBadgeClassName: null,
  idBadgeClassName: null,
  tooltipPlacement: 'bottom',
  tooltipMode: 'light',
  tooltipSize: 'xl',
};

VerificationBadges.propTypes = {
  isEmailVerified: PropTypes.bool,
  isIdVerified: PropTypes.bool,
  className: PropTypes.string,
  badgeClassName: PropTypes.string,
  emailBadgeClassName: PropTypes.string,
  idBadgeClassName: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  tooltipMode: PropTypes.string,
  tooltipSize: PropTypes.string,
};

export default VerificationBadges;

